<template>
    <div class="space-y-4">
        <label
            v-if="$slots.default || inputLabel"
            :for="'select-dropdown-' + key"
            class="text-body-sm font-semibold text-black"
            @click="focus"
        >
            <slot>{{ capitaliseFirst(inputLabel) }}</slot>
        </label>

        <slot name="after"></slot>

        <p v-if="error" class="text-semantic-error inline-flex gap-4">
            <svg-icon
                type="error"
                size="16"
            />
            <span class="text-body-sm">{{ error }}</span>
        </p>
        <popper-modal
            :id="staticId ? staticId : 'select-dropdown-' + key"
            ref="modal"
            :disabled="disabled"
            class="outline-none w-full"
            :class="{'text-black': !disabled, 'text-primary-60': disabled}"
            match-width
            role="listbox"
            @close="close"
            @open="focusSearch"
            @keydown.up.prevent="moveFocusUp"
            @keydown.down.prevent="moveFocusDown"
            @keydown.enter.prevent="handleEnterInput"
        >
            <template #trigger="{ isOpen, isFocused }">
                <div
                    class="flex items-center gap-12 rounded border outline-none py-12 px-16 min-h-44 h-44"
                    :class="[
                        {
                            'bg-primary-5 border-primary-20': disabled,
                            'border-primary-80': !disabled,
                            'border-semantic-error': error,
                            'bg-white': whiteBackground,
                        }
                    ]"
                >
                    <div v-if="icon">
                        <svg-icon
                            :class="iconColor"
                            size="16"
                            :type="icon"
                        />
                    </div>

                    <div class="min-h-24 flex items-center mr-auto overflow-x-hidden">
                        <loading v-if="loading" class="mr-auto" size="small"></loading>

                        <div v-else-if="multiple ? selection && selection.length : selection"
                             :class="{'flex items-center': !multiple, 'flex-wrap': multiple && showAllSelected}"
                             class="flex justify-start first-letter:uppercase gap-8 leading-tight w-full"
                        >
                            {{ prefix }}
                            <template v-if="!multiple">
                                <svg-icon
                                    class="text-primary-60"
                                    size="20"
                                    type="check-circle"
                                    v-if="showCheckIcon"
                                />
                                <label-with-missing-translation class="text-wrap" :label="formatLabel(selection[label])"/>
                                <div class="flex">
                                    <button
                                        v-if="removeIcon && currentValue"
                                        class="text-primary-40 ml-8 trans-ease"
                                        @click="removeSelection"
                                    >
                                        <svg-icon
                                            size="16"
                                            type="cancel"
                                        />
                                    </button>
                                </div>
                            </template>
                            <template v-else>
                                <template v-if="!showAllSelected">
                                    <tag
                                        icon="cancel"
                                        interactive
                                        icon-after
                                        @click="toggleSelect(selection[0], 0); $refs.modal._popperInstance.update();"
                                    >
                                        <label-with-missing-translation
                                            :label="formatLabel(selection[0][label])"
                                            class="text-wrap"
                                        />
                                    </tag>
                                    <tag v-if="selection.length > 1" no-icon>
                                        <span>+{{ selection.length - 1 }}</span>
                                    </tag>
                                </template>
                                <template v-else>
                                    <tag
                                        icon="cancel"
                                        interactive
                                        icon-after
                                        :key="'selected-' + index"
                                        v-for="(item, index) in selection"
                                        @click="toggleSelect(item, index); $refs.modal._popperInstance.update();"
                                    >
                                        <label-with-missing-translation
                                            :label="formatLabel(item.label)"
                                            class="text-wrap"
                                        />
                                    </tag>
                                </template>
                            </template>
                        </div>

                        <span v-else-if="placeholderVal" class="first-letter:capitalize mr-auto leading-none font-normal" :class="[disabled ? 'text-primary-60' : 'text-black']">
                            {{ placeholderVal }}
                        </span>

                        <loading v-else class="pl-16 mr-auto" size="small"></loading>
                    </div>
                    <div class="flex items-center">
                        <tooltip :content="$t('general.deselect_all')" position="top" class="mr-8"
                                 v-if="(canSelectAll || deselectable) && (multiple ? selection.length : selection)">
                            <div @click.stop.prevent="unSelectAll">
                                <svg-icon
                                    class="trans-ease transform text-primary"
                                    size="20"
                                    type="cancel-filled"
                                ></svg-icon>
                            </div>
                        </tooltip>
                        <svg-icon
                            :class="[ isOpen ? 'rotate-180' : 'rotate-0' ]"
                            class="trans-ease transform"
                            type="keyboard-arrow-down"
                        ></svg-icon>
                    </div>
                </div>
            </template>

            <template #content>
                <div class="bg-white rounded shadow border border-primary">
                    <div class="max-h-320 overflow-y-auto">
                        <div class="rounded overflow-hidden">
                            <div
                                v-if="searchable"
                                class="first-letter:uppercase cursor-pointer flex justify-between items-center sticky bg-white top-0 z-10"
                            >
                                <search
                                    :action-button-text="addNew && toggleCreateButton ? $t('general.add') : undefined"
                                    :action-button-icon="addNew && toggleCreateButton ? 'add-circle' : undefined"
                                    class="w-full"
                                    :staticId="'dropdown-search-' + _uid"
                                    :autoFocus="false"
                                    no-delete
                                    :placeholder="searchPlaceholder"
                                    ref="search"
                                    square
                                    v-model="search"
                                    @action="addNewValue"
                                >
                                    <button
                                        class="self-stretch px-12 flex items-center justify-center gap-8 flex-none cursor-pointer bg-secondary hover:bg-secondary-80 text-white"
                                        v-if="canSelectAll"
                                        role="button"
                                        @click="selectAll"
                                    >
                                        {{ $t('general.select_all') }}
                                        <svg-icon size="16" type="task-alt"/>
                                    </button>
                                </search>
                            </div>
                            <div v-if="statuses" class="w-full flex flex-row">
                                <div
                                    class="grow flex items-center justify-center p-8"
                                    :class="selectedStatus === status.value ? 'bg-primary text-white' : 'bg-white text-black hover:bg-primary-20'"
                                    :key="status.value"
                                    v-for="status in statuses"
                                    @click="selectedStatus = status.value"
                                >
                                    {{ status.label }}
                                </div>
                            </div>
                            <div
                                v-for="option in filteredOptions"
                                v-if="options"
                                :key="key + option[reference]"
                                :class="[currentValue === option[reference] ? 'bg-primary text-white cursor-default' : 'cursor-pointer', option[reference] === optionFocus ? 'bg-primary text-white' : '']"
                                :data-value="option[reference]"
                                class="px-16 py-8 first-letter:uppercase flex justify-between items-center select-none"
                                role="option"
                                @mouseenter="optionFocus = option[reference]"
                                @click.stop="handleSelection(option[reference])"
                            >
                                <div class="flex gap-8">
                                    <div v-if="option.img" class="max-w-64">
                                        <img class="object-contain" :src="option.img" :alt="option.name">
                                    </div>
                                    <span class="flex items-center">
                                        <span class="mr-6">
                                            <slot name="list-item" :item="option">
                                                <label-with-missing-translation :label="formatLabel(option[label])">
                                                    <tooltip v-if="option.icon" :content="option.icon.info" position="left">
                                                        <svg-icon
                                                            :class="option.icon.style"
                                                            class="mr-8"
                                                            size="16"
                                                            :type="option.icon.type"
                                                        />
                                                    </tooltip>
                                                </label-with-missing-translation>
                                            </slot>
                                        </span>
                                        <tooltip v-if="option[info]"
                                                 :key="option[value]"
                                                 :content="formatLabel(option[info])"
                                                 position="top">
                                            <svg-icon
                                                class="text-primary"
                                                size="12"
                                                type="info"
                                            />
                                        </tooltip>
                                    </span>
                                </div>
                                <span v-if="option.suffix" class="ml-auto">{{ option.suffix }}</span>
                                <svg-icon
                                    v-if="multiple && selection && selection.find(i => i[reference] === option[reference])"
                                    size="16"
                                    type="check"
                                />
                                <div v-if="deletable" @click.stop.prevent="triggerDelete(option[reference])">
                                    <svg-icon
                                        class="text-semantic-error-dark"
                                        :class="option[reference] === optionFocus ? 'opacity-1' : 'opacity-0'"
                                        size="16"
                                        style="transition: none;"
                                        type="delete"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </popper-modal>
    </div>
</template>

<script>
import trapFocus from '../../mixins/trapFocus';
import PopperModal from '../PopperModal.vue';
import Loading from '../ui/Loading.vue';
import SvgIcon from '../ui/SvgIcon.vue';
import uniqueId from 'lodash/uniqueId';
import Tooltip from '../navigation/Tooltip';
import Input from './Input.vue';
import LabelWithMissingTranslation from "../LabelWithMissingTranslation.vue";
import Tag from "../ui/Tag.vue";
import Search from "../ui/Search.vue";
import {ucfirst} from "../../../shared/helpers";
import Styles from "../../../shared/components/jobsite/Styles.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";

export default {
    components: {
        Styles,
        Search,
        Tag,
        Tooltip,
        'popper-modal': PopperModal,
        'svg-icon': SvgIcon,
        'loading': Loading,
        'simple-input': Input,
        LabelWithMissingTranslation,
    },
    mixins: [
        trapFocus,
    ],
    model: {
        prop: 'value',
        event: 'change',
    },
    props: {
        staticId: [String, Number],
        loading: Boolean,
        options: Array,
        value: {required: true},
        reference: {type: String, default: 'value'},
        label: {type: String, default: 'label'},
        info: {type: String, default: 'info'},
        autoTranslate: {type: Boolean, default: true},
        icon: String,
        iconColor: String,
        prefix: {type: String, default: ''},
        placeholder: {type: String},
        disabled: Boolean,
        enableWhenNoOptions: Boolean,
        multiple: Boolean,
        searchable: Boolean,
        showCheckIcon: {type: Boolean, default: false},
        addNew: {type: Boolean, default: false},
        allowEnter: {type: Boolean, default: false},
        allowSimilar: {type: Boolean, default: false},
        translatable: {type: Boolean, default: false},
        removeIcon: {type: Boolean, default: false},
        canSelectAll: {type: Boolean, default: false},
        deletable: {type: Boolean, default: false},
        deselectable: {type: Boolean, default: false},
        searchPlaceholder: {
            type: String,
            default: 'Search...'
        },
        statuses: [Array, Object],
        keepSearchAfterSelection: {type: Boolean, default: false},
        errors: Array,
        autoload: {type: Boolean, default: false},
        inputLabel: String,
        whiteBackground: Boolean,
        triggerClass: String,
        showAllSelected: Boolean,
        noAutoSort: Boolean
    },
    data: function () {
        return {
            search: '',
            optionFocus: this.value,
            key: uniqueId('option'),
            denyClose: false,
            selectedStatus: 'all',
            placeholderVal: null,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        locale() {
            return this.mainStore.locale;
        },
        currentValue: {
            get() {
                return this.value;
            },
            set(value) {
                if (value !== this.currentValue) {
                    let newValue = value;

                    if (this.multiple) {
                        if (this.selection.find(i => i[this.reference] === value)) {
                            // clicked filter is already in selection => remove from newValue
                            newValue = this.selection.filter(i => i[this.reference] !== value).map(i => i[this.reference]);
                        } else {
                            // clicked filter is not yet in selection => add to newValue
                            newValue = [...this.selection.map(i => i[this.reference]), value];
                        }
                    }

                    this.$emit('change', newValue);

                    if (!this.keepSearchAfterSelection) {
                        this.search = '';
                    }

                    if (!this.multiple) {
                        this.$refs.modal.close();
                    } else {
                        this.$refs.modal._popperInstance.update();
                    }
                }
            },
        },
        selection() {
            if (!this.options) {
                return null;
            }

            if (this.multiple) {
                let selected = [];
                this.currentValue?.forEach(value => {
                    const item = this.options.find(option => option[this.reference] === value);
                    if (item) {
                        selected.push(item);
                    }
                });
                return selected;
            } else {
                return this.options.find(option => option[this.reference] === this.currentValue);
            }
        },
        filteredOptions() {
            if (this.statuses && this.statuses.length > 0 && this.selectedStatus !== 'all') {
                const statusTranslations = {
                    published: 'jobs.published',
                    unpublished: 'jobs.unpublished',
                    archived: 'analytics.archived',
                };
                const translationKey = statusTranslations[this.selectedStatus] || '';

                return this.options.filter(item => {
                    const matchesStatus = item.status === this.$t(translationKey);
                    const matchesSearch = this.normalizeString(this.translatable ? item[this.label][this.locale] : item[this.label])?.includes(this.normalizeString(this.search));

                    return matchesStatus && matchesSearch;
                });
            }

            let list = [];

            if (!this.options || !this.search) {
                list = this.options;
            } else {
                list = this.options.filter(item =>
                    this.normalizeString(this.translatable ? item[this.label][this.locale] : item[this.label])
                        ?.includes(this.normalizeString(this.search))
                );
            }

            if (!this.noAutoSort) {
                list.sort((a, b) => {
                    let aLabel = a[this.label],
                        bLabel = b[this.label];

                    if (this.translatable) {
                        if (aLabel[this.locale] === '' && bLabel[this.locale] !== '') return 1
                        if (aLabel[this.locale] !== '' && bLabel[this.locale] === '') return -1;
                        return aLabel[this.locale]?.localeCompare(bLabel[this.locale]);
                    } else {
                        if (aLabel === '' && bLabel !== '') return 1
                        if (aLabel !== '' && bLabel === '') return -1;
                        return aLabel?.localeCompare(b[this.label]);
                    }
                });
            }

            return list
        },
        optionKeys() {
            if (!this.filteredOptions) {
                return null;
            }

            return this.filteredOptions.map(option => option[this.reference]);
        },
        toggleCreateButton() {
            if (!this.allowSimilar) {
                return !this.filteredOptions.length;
            }

            return this.search && !this.exactFilterOptionCount;
        },
        exactFilterOptionCount() {
            return this.options.filter(item => this.normalizeString(item[this.label]) === this.normalizeString(this.search)).length;
        },
        error() {
            if (!this.errors) {
                return null;
            }

            return Array.isArray(this.errors) ? this.errors[0] : this.errors;
        },
    },
    methods: {
        capitaliseFirst(text = '') {
            return ucfirst(text);
        },
        removeSelection() {
            this.currentValue = null;
            this.search = '';
            this.$emit('removedSelection');
        },
        moveFocusUp(event) {
            event.preventDefault();
            if (this.optionKeys.indexOf(this.optionFocus) <= 0) {
                this.optionFocus = this.optionKeys[this.optionKeys.length - 1];
            } else {
                this.optionFocus = this.optionKeys[this.optionKeys.indexOf(this.optionFocus) - 1];
            }
        },
        moveFocusDown() {
            if (this.optionKeys.indexOf(this.optionFocus) >= this.optionKeys.length - 1) {
                this.optionFocus = this.optionKeys[0];
            } else {
                this.optionFocus = this.optionKeys[this.optionKeys.indexOf(this.optionFocus) + 1];
            }
        },
        focus() {
            this.$refs.modal.setFocus();
        },
        focusSearch() {
            if (this.searchable) {
                this.$nextTick(() => {
                    const input = document.getElementById(this.$refs.search.$refs.input?.id);
                    if (input) {
                        input.focus();
                    }
                });
            }
        },
        open() {
            this.denyClose = true;

            setTimeout(() => {
                this.$refs.modal.open();
                this.denyClose = false;
            }, 100);
        },
        close(e) {
            if (this.denyClose) {
                return;
            }

            this.$emit('close', e);
        },
        addNewValue() {
            this.$emit('new', this.search);
            this.currentValue = this.search;
            this.search = '';
        },
        formatLabel(label) {
            if (!label) return this.$t('general.missing_translation');

            let formattedLabel = null;
            if (this.autoTranslate) {
                formattedLabel = this.$t(label);
            }
            else if (!this.translatable || typeof label === 'string') formattedLabel = label;
            else if (!!label[this.locale]) formattedLabel = label[this.locale];
            else formattedLabel = Object.values(label)[0];

            return formattedLabel;
        },
        normalizeString(string) {
            return string?.normalize('NFD')?.trim().toLowerCase().replace(/[\u0300-\u036f]/g, '');
        },
        handleSelection(value) {
            this.currentValue = value;
        },
        handleEnterInput() {
            if (this.addNew &&
                this.allowEnter &&
                !this.exactFilterOptionCount &&
                this.search) {
                this.addNewValue();
            } else if (this.currentValue !== this.optionFocus) {
                this.currentValue = this.optionFocus
            }
        },
        toggleSelect(item, index = null) {
            if (item.disabled) {
                return;
            }

            if (index !== null) {
                this.currentValue.splice(index, 1);
                this.search = null;
            } else {
                this.value.push(item);
                this.filterText = '';
                this.setFirstEnabledItem();
                this.$refs.itemSearch.focus();
            }

            this.$emit('input', this.value);
            this.$emit('selected', {
                id: item.id,
                delete: index !== null
            });
        },
        selectAll() {
            this.$emit('change', this.filteredOptions.map(i => i[this.reference]));
            this.$refs.modal._popperInstance.update();
        },
        unSelectAll() {
            this.$emit('change', this.multiple ? [] : null);
            this.$refs.modal._popperInstance.update();
        },
        triggerDelete(id) {
            this.$emit('delete', id);
        },
    },
    watch: {
        'search'() {
            this.$emit('search', this.search);
        },
    },
    mounted() {
        this.placeholderVal = this.placeholder ?? this.$t('form.dropdown.placeholder').toString();

        if (this.autoload && this.value && this.options.length === 0 && this.searchable) {
            this.$emit('search', this.value)
        }
    }
};
</script>
<style>

</style>
